import { ByteReader } from "../replay/bytes";
import { DIRS, Field, NO_ID, POS_ABSOLUTE, UnitFlags, type Depot, type Header, type Shell, type Train, paying, stations, type SiteState } from "../replay/format";
import type { Statics } from "../replay/replay";

/** Something that happened between the previous frame and this one. */
export interface Death {
  tile: number;
  faction: number;
  design: number;
}

/**
 * Everything a client knows about the world at one frame. Units are stored
 * as columns indexed by replay id. This is the boundary between where data
 * comes from (a replay file now, a live server feed later) and everything
 * that draws or inspects it.
 */
export class WorldState {
  readonly width: number;
  readonly height: number;
  readonly tiles: number;

  // Units, by id.
  readonly alive: Uint8Array;
  readonly key: BigUint64Array;
  readonly design: Uint16Array;
  readonly faction: Uint8Array;
  readonly tile: Uint32Array;
  /** Tile at the previous frame (for interpolating movement). */
  readonly prevTile: Uint32Array;
  /** Health (frames carry it in units of the design's `hp_unit`). */
  readonly hp: Uint32Array;
  readonly ammo: Uint16Array;
  readonly cargo: Uint16Array;
  /** Suppression level 0-15. */
  readonly suppression: Uint8Array;
  /** Dug-in level 0-15. */
  readonly dug: Uint8Array;
  readonly flags: Uint8Array;
  readonly target: Uint32Array;
  /** Ids of alive units, valid after `index()`. */
  live = new Uint32Array(0);

  depots: Depot[] = [];
  trains: Train[] = [];
  prevTrains = new Map<number, number>();
  shells: Shell[] = [];
  readonly corridor: Uint8Array;
  /** Goes up whenever `corridor` changes (so a big map's isn't compared
   *  every frame). */
  corridorVersion = 0;
  /** Industry, when the replay has it: per site (header order), and freight
   *  per station depot index. */
  sites: SiteState[] = [];
  freight = new Map<number, Uint32Array>();
  /** Barracks that pay for their units, by producer: waiting (couldn't pay
   *  at its last turn), and the steel and recruits at its station (recruits
   *  null in replays before v28). */
  barracks = new Map<number, { waiting: boolean; steel: number; recruits: number | null }>();
  private payingList: number[] = [];
  private readonly stationList: number[];

  tick = 0;
  /** Index of the frame this state holds (-1 before the first). */
  frame = -1;
  /** Deaths and shell impacts between the previous frame and this one. */
  deaths: Death[] = [];
  impacts: Shell[] = [];

  constructor(
    readonly header: Header,
    readonly statics: Statics,
  ) {
    const n = header.unit_ids;
    this.width = header.world.width;
    this.height = header.world.height;
    this.tiles = this.width * this.height;
    this.alive = new Uint8Array(n);
    this.key = new BigUint64Array(n);
    this.design = new Uint16Array(n);
    this.faction = new Uint8Array(n);
    this.tile = new Uint32Array(n);
    this.prevTile = new Uint32Array(n);
    this.hp = new Uint32Array(n);
    this.ammo = new Uint16Array(n);
    this.cargo = new Uint16Array(n);
    this.suppression = new Uint8Array(n);
    this.dug = new Uint8Array(n);
    this.flags = new Uint8Array(n);
    this.target = new Uint32Array(n).fill(NO_ID);
    this.corridor = statics.corridor.slice();
    this.stationList = stations(header);
    this.payingList = paying(header);
  }

  /** Makes the current positions the "previous" ones (no motion to show). */
  settle(): void {
    this.prevTile.set(this.tile);
    this.prevTrains = new Map(this.trains.map((t) => [t.id, t.tile]));
    this.deaths = [];
    this.impacts = [];
  }

  /** Applies the next frame from `r`. */
  apply(r: ByteReader): void {
    this.prevTile.set(this.tile);
    this.prevTrains = new Map(this.trains.map((t) => [t.id, t.tile]));
    this.deaths = [];
    this.tick = r.u32();
    const key = r.u8() === 1;
    if (key) {
      this.alive.fill(0);
      this.corridor.set(this.statics.corridor);
      this.corridorVersion++;
      const ids = r.ids();
      for (const id of ids) {
        this.alive[id] = 1;
        this.target[id] = NO_ID;
      }
      this.columns(r, ids, null, true);
      // A keyframe isn't motion: nothing to interpolate from.
      this.prevTile.set(this.tile);
    } else {
      const removed = r.ids();
      // Fogged: units that left sight rather than died.
      const faded = this.header.fog === undefined ? null : new Set(r.ids());
      for (const id of removed) {
        // Convoy trucks home and prisoners handed over leave the map without
        // dying: no death mark for them (nor for units leaving sight).
        const leaving = (this.flags[id] & UnitFlags.CONVOY) !== 0 || this.header.designs[this.design[id]]?.damage === 0 || !!faded?.has(id);
        if (!leaving) this.deaths.push({ tile: this.tile[id], faction: this.faction[id], design: this.design[id] });
        this.alive[id] = 0;
      }
      const ids = r.ids();
      const masks = r.bytes.subarray(r.at, r.at + ids.length);
      r.at += ids.length;
      for (let i = 0; i < ids.length; i++) {
        const id = ids[i];
        if (!this.alive[id]) {
          this.alive[id] = 1;
          this.target[id] = NO_ID;
          // New units appear where they are, not sliding in from 0.
          if (masks[i] & Field.POS) this.prevTile[id] = NO_ID;
        }
      }
      this.columns(r, ids, masks, false);
      for (const id of ids) if (this.prevTile[id] === NO_ID) this.prevTile[id] = this.tile[id];
    }

    this.depots = [];
    for (let i = 0, n = r.u32(); i < n; i++) {
      const [tile, faction, stock, capacity, kind] = [r.u32(), r.u8(), r.u32(), r.u32(), r.u8()];
      this.depots.push({ tile, faction, stock, capacity, field: kind === 1, kind });
    }
    this.trains = [];
    for (let i = 0, n = r.u32(); i < n; i++) {
      this.trains.push({ id: r.u32(), tile: r.u32(), faction: r.u8(), flags: r.u8() });
    }
    const before = this.shells;
    this.shells = [];
    for (let i = 0, n = r.u32(); i < n; i++) this.shells.push({ from: r.u32(), tile: r.u32(), impact: r.u32() });
    const still = new Set(this.shells.map(shellKey));
    this.impacts = key ? [] : before.filter((s) => !still.has(shellKey(s)) && s.impact <= this.tick);
    for (let i = 0, n = r.u32(); i < n; i++) {
      const t = r.u32();
      this.corridor[t] = r.u8();
      this.corridorVersion++;
    }
    const sites = this.header.sites ?? [];
    if (sites.length) {
      this.sites = sites.map(() => ({ integrity: r.u32(), output: r.u32(), reserve: r.u32() }));
      const goods = this.header.freight_goods?.length ?? 0;
      for (const st of this.stationList) {
        const row = new Uint32Array(goods);
        for (let g = 0; g < goods; g++) row[g] = r.u32();
        this.freight.set(st, row);
      }
    }
    const recruits = !!this.header.recruits;
    for (const p of this.payingList) this.barracks.set(p, { waiting: r.u8() !== 0, steel: r.u32(), recruits: recruits ? r.u32() : null });
    this.frame++;
  }

  private columns(r: ByteReader, ids: Uint32Array, masks: Uint8Array | null, key: boolean): void {
    const has = (i: number, bit: number) => masks === null || (masks[i] & bit) !== 0;
    let lastKey = 0n;
    for (let i = 0; i < ids.length; i++) {
      if (!has(i, Field.STATIC)) continue;
      const id = ids[i];
      lastKey = BigInt.asUintN(64, lastKey + r.svarBig());
      this.key[id] = lastKey;
      this.design[id] = r.var();
      this.faction[id] = r.u8();
    }
    let lastTile = 0;
    const w = this.width;
    for (let i = 0; i < ids.length; i++) {
      if (!has(i, Field.POS)) continue;
      const id = ids[i];
      if (key) {
        lastTile += r.svar();
        this.tile[id] = lastTile;
      } else {
        const code = r.u8();
        if (code === POS_ABSOLUTE) {
          this.tile[id] = r.var();
        } else {
          const t = this.tile[id];
          const [dq, dr] = DIRS[code];
          this.tile[id] = ((t / w) | 0) * w + dr * w + (t % w) + dq;
        }
      }
    }
    for (let i = 0; i < ids.length; i++) if (has(i, Field.HP)) this.hp[ids[i]] = r.u16() * (this.header.designs[this.design[ids[i]]]?.hp_unit ?? 1);
    for (let i = 0; i < ids.length; i++) if (has(i, Field.AMMO)) this.ammo[ids[i]] = r.u16();
    for (let i = 0; i < ids.length; i++) if (has(i, Field.CARGO)) this.cargo[ids[i]] = r.u16();
    for (let i = 0; i < ids.length; i++) {
      if (!has(i, Field.STATUS)) continue;
      const b = r.u8();
      this.suppression[ids[i]] = b >> 4;
      this.dug[ids[i]] = b & 15;
      this.flags[ids[i]] = r.u8();
    }
    for (let i = 0; i < ids.length; i++) {
      if (!has(i, Field.TARGET)) continue;
      const v = r.var();
      this.target[ids[i]] = v === 0 ? NO_ID : v - 1;
    }
  }

  /** Rebuilds `live`, the list of alive ids. */
  index(): void {
    let n = 0;
    for (let id = 0; id < this.alive.length; id++) if (this.alive[id]) n++;
    const live = new Uint32Array(n);
    n = 0;
    for (let id = 0; id < this.alive.length; id++) if (this.alive[id]) live[n++] = id;
    this.live = live;
  }

  q(tile: number): number {
    return tile % this.width;
  }

  r(tile: number): number {
    return (tile / this.width) | 0;
  }

  /** Game time in seconds since the start of the replay. */
  seconds(): number {
    return (this.tick - this.header.first_tick) / this.header.tick_rate;
  }
}

function shellKey(s: Shell): string {
  return `${s.from}:${s.tile}:${s.impact}`;
}
